import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

type ToastTone = 'success' | 'error' | 'info';
export type ToastInput = {
  title: string;
  description?: ReactNode;
  tone?: ToastTone;
  action?: { label: string; onClick: () => void };
  duration?: number;
};
type ToastItem = ToastInput & { id: number; leaving?: boolean };

type ToastApi = {
  show: (t: ToastInput) => number;
  success: (title: string, description?: ReactNode, action?: ToastInput['action']) => number;
  error: (title: string, description?: ReactNode) => number;
  info: (title: string, description?: ReactNode, action?: ToastInput['action']) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('ToastProvider відсутній');
  return ctx;
}

let seq = 0;

/** Сповіщення про результат дії: з'являються знизу, пауза при наведенні, закриття кнопкою або Esc у фокусі. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, number>());

  const remove = useCallback((id: number) => {
    setItems((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(() => setItems((list) => list.filter((t) => t.id !== id)), 160);
    const tm = timers.current.get(id);
    if (tm) window.clearTimeout(tm);
    timers.current.delete(id);
  }, []);

  const schedule = useCallback(
    (id: number, ms: number) => {
      const tm = window.setTimeout(() => remove(id), ms);
      timers.current.set(id, tm);
    },
    [remove],
  );

  const show = useCallback(
    (t: ToastInput) => {
      const id = ++seq;
      const duration = t.duration ?? (t.tone === 'error' ? 9000 : 5000);
      setItems((list) => [...list.slice(-3), { ...t, id }]);
      schedule(id, duration);
      return id;
    },
    [schedule],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, description, action) => show({ title, description, tone: 'success', action }),
      error: (title, description) => show({ title, description, tone: 'error' }),
      info: (title, description, action) => show({ title, description, tone: 'info', action }),
      dismiss: remove,
    }),
    [show, remove],
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((tm) => window.clearTimeout(tm));
  }, []);

  const pause = (id: number) => {
    const tm = timers.current.get(id);
    if (tm) window.clearTimeout(tm);
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        data-keep-panel=""
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+72px)] z-[80] flex flex-col items-center gap-2 px-4 sm:bottom-5 sm:items-start sm:px-5 lg:left-[var(--sidebar-w)]"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            data-leaving={t.leaving ? '' : undefined}
            onPointerEnter={() => pause(t.id)}
            onPointerLeave={() => schedule(t.id, 3000)}
            onFocus={() => pause(t.id)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') remove(t.id);
            }}
            className="ers-toast pointer-events-auto flex w-full max-w-[400px] items-start gap-3 rounded-md border border-line bg-raised px-3.5 py-3 text-fg shadow-pop"
          >
            <span className="mt-0.5 shrink-0">
              {t.tone === 'success' ? (
                <CheckCircle2 className="size-4 text-success" aria-hidden />
              ) : t.tone === 'error' ? (
                <AlertCircle className="size-4 text-danger" aria-hidden />
              ) : (
                <Info className="size-4 text-info" aria-hidden />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium">{t.title}</p>
              {t.description && <div className="mt-0.5 text-meta text-fg-2">{t.description}</div>}
              {t.action && (
                <button
                  type="button"
                  onClick={() => {
                    t.action!.onClick();
                    remove(t.id);
                  }}
                  className="mt-1.5 text-meta font-medium text-accent-fg underline-offset-2 hover:underline"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(t.id)}
              aria-label="Закрити сповіщення"
              className={cn('-mt-1 -mr-1.5 inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-fg-3 hover:bg-surface-2 hover:text-fg')}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
